/*
 *  -----------------------------------------------------------------------
 *    <copyright file="delete-indication-dialog.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';

import { useBoolean, useTranslation } from 'hooks';
import { ASSIGN_INDICATIONS, GET_STUDY } from 'queries/studies';
import NotificationService from 'utils/notification';
import Button from 'views/components/core/buttons';
import { CustomDialog, DialogTitle, DialogContent, DialogActions } from 'views/components/core/dialog';

import 'views/components/admin/studies/admin-study.scss';

const DeleteIndicationDialog = ({ open, onClose, currentIndication, study }: any) => {
    const { t } = useTranslation();
    const saving = useBoolean(false);
    const [assignIndications] = useMutation(ASSIGN_INDICATIONS);

    const close = (action?: any) => {
        onClose(action);
    };

    const errorRequest = () => {
        NotificationService.showNotification(t('errorMessage'));
        saving.setFalse();
    };

    const successRequest = (closeModal: boolean) => {
        saving.setFalse();
        NotificationService.showNotification(
            t('admin:indicationSuccessfullyUnassigned', {
                studyId: study.recordId,
                id: currentIndication?.recordId ?? currentIndication?.id,
            }),
        );
        if (closeModal) onClose();
    };

    const onDeleteIndication = (closeModal?: boolean) => {
        const indicationIndex = study.indicationsIds.findIndex(
            (indicationId: any) => indicationId === currentIndication.id,
        );
        const studyIndicationIds = Object.assign([], study.indicationsIds);
        studyIndicationIds.splice(indicationIndex, 1);

        saving.setTrue();

        assignIndications({
            variables: { studyId: study.id, reason: '', studyIndicationIds },
            refetchQueries: [{ query: GET_STUDY, variables: { id: study.id } }],
        })
            .then(() => {
                successRequest(closeModal);
            })
            .catch(() => {
                errorRequest();
            });
    };

    return (
        <CustomDialog onClose={close} fullWidth scroll="paper" maxWidth="md" open={open} className="custom-width-popup">
            <DialogTitle id="delete-indication-modal-title">
                {t('admin:unassignIndication')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={close}
                    data-testid="close-dialog"
                />
            </DialogTitle>

            <DialogContent className="delete-indication-dialog-content">
                <>
                    <div>
                        <span className="confirm-text pb">{t('admin:areYouSureYouWantToDeleteThisIndication')}</span>
                    </div>
                </>
            </DialogContent>

            <DialogActions className="delete-indication dialog-actions flex-end">
                <Button
                    disabled={saving.value}
                    id="delete-indication-save"
                    text="unassign"
                    secondary
                    onClick={onDeleteIndication}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default DeleteIndicationDialog;
